if (!customElements.get("add-to-wishlist-button")) {
  customElements.define(
    "add-to-wishlist-button",
    class AddToWishlistButton extends HTMLElement {
      constructor() {
        super();
        this.addText = "Add to wishlist";
        this.removeText = "Remove from wishlist";
        this.state = null;
        this.productInWishlist = false;
        this.isInitializing = true;
      }
      connectedCallback() {
        if (!window.wishlistMigrationDone) {
          window.wishlistMigrationDone = true;
          if (!localStorage.getItem("wishlistV2")) {
            localStorage.setItem("wishlistV2", "1");
            try {
              const stored = localStorage.getItem("wishlists");
              if (stored) {
                const wishlists = JSON.parse(stored);
                const hasCustomerData =
                  Array.isArray(wishlists) &&
                  wishlists.some(
                    (w) =>
                      w?.wishlist_row_id &&
                      !String(w.wishlist_row_id).startsWith("local-wishlist-"),
                  );
                if (hasCustomerData) {
                  localStorage.removeItem("wishlists");
                  localStorage.removeItem("wishlistsRemovedItems");
                  localStorage.removeItem("wishlistsRemovedWishlists");
                  window.wishlists = undefined;
                }
              }
            } catch (_e) {}
          }
        }

        this.button = this.querySelector("button");

        if (!this.button) {
          console.warn(
            "Wishlist button not found in add-to-wishlist-button element",
          );
          return;
        }

        this.buttonText = this.button.querySelector(
          ".add-to-wishlist-button__text",
        );
        this.iconEmpty = this.querySelector(".icon-wishlist-empty");
        this.iconFilled = this.querySelector(".icon-wishlist-filled");
        this.handleButtonClickHandler = this.handleButtonClick.bind(this);
        this.button.addEventListener("click", this.handleButtonClickHandler);
        this.init();
      }
      disconnectedCallback() {
        if (this.button) {
          this.button.removeEventListener(
            "click",
            this.handleButtonClickHandler,
          );
        }
        this.stopCartMobilePopoverTracking();
        this.togglePopoverStackingContext(false);
        if (this.handleDocumentClickHandler) {
          document.removeEventListener(
            "click",
            this.handleDocumentClickHandler,
          );
        }
        // Clean up any teleported popover that was moved to document.body
        if (this.wishlistPopover?.parentElement === document.body) {
          document.body.removeChild(this.wishlistPopover);
        }
      }
      async init() {
        // if (typeof this.customerId == "undefined") this.customerId = null;
        
        await this.getWishlists();
        this.isInitializing = false;
      }
      get loadingSpinner() {
        return this.querySelector(".loading__spinner");
      }
      set productId(productId) {
        this.dataset.productId = productId;
      }
      get productId() {
        return this.dataset.productId;
      }
      set productHandle(productHandle) {
        this.dataset.productHandle = productHandle;
      }
      get productHandle() {
        return this.dataset.productHandle;
      }
      set variantId(variantId) {
        this.dataset.variantId = variantId;
      }
      get variantId() {
        return this.dataset.variantId;
      }
      set customerId(customerId) {
        this.dataset.customerId = customerId;
      }
      get customerId() {
        return this.dataset.customerId;
      }
      normalizeOnSaleValue(onSale) {
        if (typeof onSale === "boolean") return onSale;
        if (typeof onSale === "number") return onSale > 0;
        if (typeof onSale !== "string") return false;

        const normalizedOnSale = onSale.trim().toLowerCase();
        if (!normalizedOnSale || normalizedOnSale === "false") return false;
        if (!Number.isNaN(Number(normalizedOnSale))) {
          return Number(normalizedOnSale) > 0;
        }

        return ["true", "1", "on-sale", "on_sale", "sale"].includes(
          normalizedOnSale,
        );
      }
      set onSale(onSale) {
        const onSaleValue = this.normalizeOnSaleValue(onSale)
          ? "true"
          : "false";
        this.dataset.onSale = onSaleValue;
        if (this.button) {
          this.button.dataset.onSale = onSaleValue;
        }
      }
      get onSale() {
        const onSaleValue = this.button?.dataset.onSale ?? this.dataset.onSale;
        return this.normalizeOnSaleValue(onSaleValue);
      }
      changeText(text) {
        if (!this.buttonText) return;

        this.buttonText.textContent = text;
      }
      setLoadingState() {
        this.button.classList.add("loading");
        if (this.loadingSpinner) {
          // Don't show spinner in cart during initial load
          const isInCart = this.closest(".cart-item") || this.closest(".remove-wishlist-wrapper");
          if (isInCart && this.isInitializing) {
            return;
          }
          
          this.loadingSpinner.classList.remove("hidden");
        }
      }
      removeLoadingState() {
        this.button.classList.remove("loading");
        if (this.loadingSpinner) {
          this.loadingSpinner.classList.add("hidden");
        }
      }
      applyState(state) {
        const isAdded = state === "added";
        this.state = isAdded ? "added" : "removed";
        this.productInWishlist = isAdded;

        if (this.iconEmpty) {
          this.iconEmpty.style.display = isAdded ? "none" : "block";
        }
        if (this.iconFilled) {
          this.iconFilled.style.display = isAdded ? "block" : "none";
        }
        this.changeText(isAdded ? this.removeText : this.addText);
      }
      updateState(state) {
        const isProductTemplatePage =
          window.location.pathname.includes("/products/");

        if (!isProductTemplatePage) {
          this.applyState(state);
          return;
        }

        const buttons = document.querySelectorAll("add-to-wishlist-button");
        buttons.forEach((button) => {
          if (
            typeof button.applyState === "function" &&
            button.productId === this.productId
          ) {
            button.applyState(state);
          }
        });
      }
      updateHeaderButtonCount(inc) {
        const wishlistHeaderButton = Array.from(
          document.querySelectorAll("wishlist-header-button"),
        );
        if (!wishlistHeaderButton || wishlistHeaderButton.length === 0) return;

        if (inc) {
          wishlistHeaderButton.forEach((button) => {
            button.increment();
          });
        } else {
          wishlistHeaderButton.forEach((button) => {
            button.decrement();
          });
        }
      }
      updateWishlistViewer() {
        const wishlistViewer = document.querySelector("wishlist-viewer");
        if (!wishlistViewer) return;
        if (wishlistViewer.contains(this)) return;

        wishlistViewer.init();
      }
      rerenderWishlistCarousels() {
        const wishlistCarousels =
          document.querySelectorAll("wishlist-carousel");
        if (!wishlistCarousels.length) return;

        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            wishlistCarousels.forEach((carousel) => {
              if (typeof carousel.init === "function") {
                void carousel.init();
              }
            });
          });
        });
      }
      isCartPage() {
        return Boolean(
          document.getElementById("main-cart-items") ||
          document.getElementById("CartDrawer-CartItems"),
        );
      }
      isCartTemplatePage() {
        return Boolean(document.getElementById("main-cart-items"));
      }
      isInsideProductSlider() {
        return Boolean(this.closest("product-slider"));
      }
      shouldUseCartMobilePopoverLayout() {
        return (
          this.isInsideProductSlider() ||
          (this.isCartTemplatePage() &&
            window.innerWidth < 450 &&
            Boolean(this.closest(".cart-item")))
        );
      }
      updateCartMobilePopoverPosition() {
        if (!this.wishlistPopover || !this.button) return;

        const shouldUseMobileLayout = this.shouldUseCartMobilePopoverLayout();
        this.wishlistPopover.classList.toggle(
          "add-to-wishlist-button__popover--cart-mobile",
          shouldUseMobileLayout,
        );

        if (!shouldUseMobileLayout) {
          this.wishlistPopover.style.removeProperty("position");
          this.wishlistPopover.style.removeProperty("top");
          this.wishlistPopover.style.removeProperty("left");
          this.wishlistPopover.style.removeProperty("width");
          this.wishlistPopover.style.removeProperty("min-width");
          this.wishlistPopover.style.removeProperty("max-width");
          this.wishlistPopover.style.removeProperty("max-height");
          this.wishlistPopover.style.removeProperty("overflow-y");
          this.wishlistPopover.style.removeProperty("transform");
          this.wishlistPopover.style.removeProperty("border-radius");
          this.wishlistPopover.style.removeProperty("z-index");
          return;
        }

        const buttonBounds = this.button.getBoundingClientRect();

        if (this.isInsideProductSlider()) {
          // Use fixed positioning aligned below the button so the popover
          // escapes the slider's overflow-y: hidden clipping context.
          this.wishlistPopover.style.position = "fixed";
          this.wishlistPopover.style.top = `${Math.round(buttonBounds.bottom + 8)}px`;
          this.wishlistPopover.style.left = `${Math.round(buttonBounds.left)}px`;
          this.wishlistPopover.style.width = `${Math.round(buttonBounds.width)}px`;
          this.wishlistPopover.style.removeProperty("min-width");
          this.wishlistPopover.style.removeProperty("max-width");
          this.wishlistPopover.style.removeProperty("max-height");
          this.wishlistPopover.style.removeProperty("overflow-y");
          this.wishlistPopover.style.removeProperty("transform");
          this.wishlistPopover.style.removeProperty("border-radius");
          this.wishlistPopover.style.zIndex = "101";
          return;
        }

        const topOffset = Math.max(16, Math.round(buttonBounds.bottom + 8));
        const maxHeight = Math.max(180, window.innerHeight - topOffset - 16);
        this.wishlistPopover.style.position = "fixed";
        this.wishlistPopover.style.top = `${topOffset}px`;
        this.wishlistPopover.style.left = "50%";
        this.wishlistPopover.style.width = `${window.innerWidth}px`;
        this.wishlistPopover.style.minWidth = "0";
        this.wishlistPopover.style.maxWidth = `${window.innerWidth}px`;
        this.wishlistPopover.style.maxHeight = `${maxHeight}px`;
        this.wishlistPopover.style.overflowY = "auto";
        this.wishlistPopover.style.transform = "translateX(-50%)";
        this.wishlistPopover.style.borderRadius = "0";
        this.wishlistPopover.style.zIndex = "101";
      }
      startCartMobilePopoverTracking() {
        if (this.cartMobilePopoverTracking) {
          this.updateCartMobilePopoverPosition();
          return;
        }

        this.cartMobilePopoverTracking = true;
        this.boundUpdateCartMobilePopoverPosition =
          this.boundUpdateCartMobilePopoverPosition ||
          this.updateCartMobilePopoverPosition.bind(this);

        window.addEventListener(
          "resize",
          this.boundUpdateCartMobilePopoverPosition,
        );
        window.addEventListener(
          "scroll",
          this.boundUpdateCartMobilePopoverPosition,
          { passive: true },
        );

        this.updateCartMobilePopoverPosition();
      }
      stopCartMobilePopoverTracking() {
        if (this.boundUpdateCartMobilePopoverPosition) {
          window.removeEventListener(
            "resize",
            this.boundUpdateCartMobilePopoverPosition,
          );
          window.removeEventListener(
            "scroll",
            this.boundUpdateCartMobilePopoverPosition,
          );
        }

        this.cartMobilePopoverTracking = false;

        if (this.wishlistPopover) {
          this.wishlistPopover.classList.remove(
            "add-to-wishlist-button__popover--cart-mobile",
          );
          this.wishlistPopover.style.removeProperty("position");
          this.wishlistPopover.style.removeProperty("top");
          this.wishlistPopover.style.removeProperty("left");
          this.wishlistPopover.style.removeProperty("width");
          this.wishlistPopover.style.removeProperty("min-width");
          this.wishlistPopover.style.removeProperty("max-width");
          this.wishlistPopover.style.removeProperty("max-height");
          this.wishlistPopover.style.removeProperty("overflow-y");
          this.wishlistPopover.style.removeProperty("transform");
          this.wishlistPopover.style.removeProperty("border-radius");
          this.wishlistPopover.style.removeProperty("z-index");
        }
      }
      getCartLineIndex() {
        const cartItem = this.closest(".cart-item");
        if (!cartItem) return null;

        const removeButton = cartItem.querySelector("cart-remove-button");
        const quantityInput = cartItem.querySelector("[data-index]");
        const lineIndex =
          removeButton?.dataset.index ||
          quantityInput?.dataset.index ||
          cartItem.id?.match(/(\d+)$/)?.[1] ||
          null;

        return lineIndex ? Number(lineIndex) : null;
      }
      async removeItemFromCart() {
        const line = this.getCartLineIndex();
        if (!line) return false;

        const response = await fetch(
          `${Shopify.routes.root}cart/change.js`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              line,
              quantity: 0,
            }),
          },
        );

        if (!response.ok) {
          throw new Error(
            `Request failed (${response.status}) for /cart/change.js`,
          );
        }

        const cartData = await response.json();

        if (
          typeof publish === "function" &&
          typeof PUB_SUB_EVENTS !== "undefined" &&
          PUB_SUB_EVENTS?.cartUpdate
        ) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "wishlist",
            cartData,
            variantId: this.variantId,
            productVariantId: this.variantId,
          });
        }

        return true;
      }
      async handleCartPageWishlistAdd() {
        if (!this.isCartPage()) return;

        try {
          await this.removeItemFromCart();
        } catch (error) {
          console.error("Error removing wishlist item from cart", error);
        } finally {
          this.rerenderWishlistCarousels();
        }
      }
      notifyWishlistViewer(action) {
        this.dispatchEvent(
          new CustomEvent("wishlist:changed", {
            bubbles: true,
            detail: {
              action,
              productId: this.productId,
              variantId: this.variantId,
            },
          }),
        );
      }

      saveWishlists() {
        if (this.customerId) return;
        const normalizedWishlists = this.normalizeWishlists(window.wishlists);
        window.wishlists = normalizedWishlists;
        localStorage.setItem("wishlists", JSON.stringify(normalizedWishlists));
      }
      get wishlistBaseUrl() {
        return "https://rarewaves-wishlist.etempashopifyapps-server-b.co.uk";
      }
      async fetchJson(url, body) {
        const requestUrl = url.startsWith("http")
          ? url
          : `${this.wishlistBaseUrl}${url}`;
        const response = await fetch(requestUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          throw new Error(
            `Request failed (${response.status}) for ${requestUrl}`,
          );
        }

        const rawResponse = await response.text();
        let data;
        try {
          data = JSON.parse(rawResponse);
        } catch (parseError) {
          throw new Error(
            `Invalid JSON from ${requestUrl}: ${parseError.message}. Response: ${rawResponse.slice(0, 300)}`,
          );
        }

        if (data.failed) {
          throw new Error(
            `Endpoint error from ${requestUrl}: ${data.error_message || "Unknown error"}`,
          );
        }

        return data;
      }
      loadStorageArray(key) {
        const storedValue = localStorage.getItem(key);
        if (!storedValue) {
          return [];
        }

        try {
          const parsedValue = JSON.parse(storedValue);
          return Array.isArray(parsedValue)
            ? parsedValue.map((item) => String(item)).filter(Boolean)
            : [];
        } catch (error) {
          console.error(`Failed to parse ${key}`, error);
          return [];
        }
      }
      loadRemovedItemIds() {
        return this.loadStorageArray("wishlistsRemovedItems");
      }
      loadDeletedWishlistRowIds() {
        return this.loadStorageArray("wishlistsRemovedWishlists");
      }
      saveDeletedWishlistRowIds(rowIds) {
        const normalizedRowIds = Array.from(
          new Set((rowIds || []).map((rowId) => String(rowId)).filter(Boolean)),
        );

        if (normalizedRowIds.length === 0) {
          localStorage.removeItem("wishlistsRemovedWishlists");
          return;
        }

        localStorage.setItem(
          "wishlistsRemovedWishlists",
          JSON.stringify(normalizedRowIds),
        );
      }
      async syncDeletedWishlists() {
        const deletedWishlistRowIds = this.loadDeletedWishlistRowIds();
        if (deletedWishlistRowIds.length === 0) {
          return;
        }

        const settledResults = await Promise.allSettled(
          deletedWishlistRowIds.map((wishlistRowId) =>
            this.fetchJson("/endpoints/remove_wishlist.php", {
              wishlistRowId,
            }),
          ),
        );
        const failedRowIds = deletedWishlistRowIds.filter(
          (_wishlistRowId, index) =>
            settledResults[index]?.status !== "fulfilled",
        );

        this.saveDeletedWishlistRowIds(failedRowIds);
      }
      async syncGuestWishlistsOnLogin() {
        if (window.wishlistGuestSyncComplete) {
          return;
        }

        if (window.wishlistGuestSyncPromise) {
          await window.wishlistGuestSyncPromise;
          return;
        }

        if (window.wishlistGuestSyncAttempted) {
          return;
        }

        window.wishlistGuestSyncAttempted = true;
        window.wishlistGuestSyncPromise = (async () => {
          const localWishlists = this.loadLocalWishlists();
          const removedItems = this.loadRemovedItemIds();
          const deletedWishlistRowIds = this.loadDeletedWishlistRowIds();

          if (localWishlists.length > 0 || removedItems.length > 0) {
            await this.fetchJson("/endpoints/save_new_customer_wishlists.php", {
              shopify_customer_id: this.customerId,
              wishlists: localWishlists,
              removed_items: removedItems,
            });
          }

          if (deletedWishlistRowIds.length > 0) {
            await this.syncDeletedWishlists();
          }

          localStorage.removeItem("wishlists");
          localStorage.removeItem("wishlistsRemovedItems");
          localStorage.removeItem("wishlistsRemovedWishlists");
          window.wishlistGuestSyncComplete = true;
        })();

        try {
          await window.wishlistGuestSyncPromise;
        } finally {
          window.wishlistGuestSyncPromise = null;
        }
      }
      formatCreatedAt(date = new Date()) {
        const pad = (value) => String(value).padStart(2, "0");
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
      }
      normalizeWishlistId(value) {
        if (value === "" || value == null) return null;

        const normalizedValue = Number(value);
        return Number.isFinite(normalizedValue) ? normalizedValue : null;
      }
      normalizeWishlistItem(item) {
        if (!item || typeof item !== "object") return item;

        return {
          ...item,
          product_id: this.normalizeWishlistId(
            item.product_id ?? item.productId,
          ),
          variant_id: this.normalizeWishlistId(
            item.variant_id ?? item.variantId,
          ),
          created_at: item.created_at || this.formatCreatedAt(),
        };
      }
      sanitizeWishlistName(name) {
        const sanitizedName = String(name ?? "")
          .replace(/[^\x20-\x7E]/g, "")
          .trim();

        return sanitizedName || "Default";
      }
      decodeWishlistName(name) {
        const textarea = document.createElement("textarea");
        textarea.innerHTML = String(name ?? "");

        return textarea.value;
      }
      sanitizeWishlistNameInput() {
        if (!this.wishlistNameInput) return;

        const selectionStart = this.wishlistNameInput.selectionStart;
        const valueBeforeCursor = this.wishlistNameInput.value.slice(
          0,
          selectionStart ?? 0,
        );
        const sanitizedValue = this.wishlistNameInput.value.replace(
          /[^\x20-\x7E]/g,
          "",
        );

        if (sanitizedValue === this.wishlistNameInput.value) return;

        const sanitizedBeforeCursor = valueBeforeCursor.replace(
          /[^\x20-\x7E]/g,
          "",
        );
        this.wishlistNameInput.value = sanitizedValue;
        this.wishlistNameInput.setSelectionRange(
          sanitizedBeforeCursor.length,
          sanitizedBeforeCursor.length,
        );
      }
      generateLocalWishlistRowId(existingIds = new Set()) {
        let wishlistRowId = "";

        if (window.crypto && typeof window.crypto.randomUUID === "function") {
          wishlistRowId = `local-wishlist-${window.crypto.randomUUID()}`;
        } else {
          wishlistRowId = `local-wishlist-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        }

        while (existingIds.has(wishlistRowId)) {
          wishlistRowId = `local-wishlist-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        }

        return wishlistRowId;
      }
      normalizeWishlist(wishlist, existingIds = new Set()) {
        if (!wishlist || typeof wishlist !== "object") return wishlist;

        const wishlistItems = Array.isArray(wishlist.items)
          ? wishlist.items
          : Array.isArray(wishlist[3])
            ? wishlist[3]
            : [];
        let wishlistRowId = wishlist.wishlist_row_id ?? wishlist[1] ?? null;
        if (!wishlistRowId || existingIds.has(String(wishlistRowId))) {
          wishlistRowId = this.generateLocalWishlistRowId(existingIds);
        }

        existingIds.add(String(wishlistRowId));

        return {
          ...wishlist,
          wishlist_name: this.sanitizeWishlistName(
            wishlist.wishlist_name ?? wishlist[0],
          ),
          wishlist_row_id: wishlistRowId,
          items: wishlistItems.map((item) => this.normalizeWishlistItem(item)),
        };
      }
      normalizeWishlists(wishlists) {
        const existingIds = new Set();
        return Array.isArray(wishlists)
          ? wishlists.map((wishlist) =>
              this.normalizeWishlist(wishlist, existingIds),
            )
          : [];
      }
      buildWishlistItem(overrides = {}) {
        return this.normalizeWishlistItem({
          product_id: this.productId,
          variant_id: this.variantId,
          product_handle: this.dataset.productHandle,
          ...overrides,
        });
      }
      renderWishlistList() {
        if (!this.wishlistList) return;

        this.wishlistList.innerHTML = "";

        (window.wishlists || []).forEach((wishlist) => {
          const item = document.createElement("li");
          item.className = "add-to-wishlist-button__popover-item";
          item.textContent = this.decodeWishlistName(wishlist.wishlist_name);
          item.dataset.wishlistName = wishlist.wishlist_name;
          item.dataset.wishlistRowId = wishlist.wishlist_row_id ?? "";
          this.wishlistList.appendChild(item);
        });

        if (this.newWishlistButton && !this.customerId) {
          this.newWishlistButton.style.display =
            (window.wishlists || []).length >= 1 ? "none" : "";
        }
      }
      addWishlist(name) {
        if (!this.customerId && (window.wishlists || []).length >= 1) {
          return;
        }
        const wishlistName = this.sanitizeWishlistName(name);

        if (!Array.isArray(window.wishlists)) {
          window.wishlists = [];
        }

        window.wishlists.push({
          wishlist_name: wishlistName,
          wishlist_row_id: this.generateLocalWishlistRowId(
            new Set(
              (window.wishlists || [])
                .map((wishlist) => String(wishlist?.wishlist_row_id ?? ""))
                .filter(Boolean),
            ),
          ),
          items: [],
        });
        this.saveWishlists();
        this.renderWishlistList();
        this.closeWishlistPopover();
      }
      closeWishlistPopover() {
        if (!this.wishlistPopover) return;
        this.wishlistPopover.classList.remove("is-visible");
        this.stopCartMobilePopoverTracking();
        this.togglePopoverStackingContext(false);
        this.button?.setAttribute("aria-expanded", "false");
        if (this.wishlistCreateContainer) {
          this.wishlistCreateContainer.classList.remove("is-visible");
        }
      }
      togglePopoverStackingContext(isOpen) {
        const cardWrapper = this.closest(".card-wrapper");
        const gridItem = this.closest(".grid__item");

        if (cardWrapper) {
          cardWrapper.classList.toggle("wishlist-popover-open", isOpen);
        }
        if (gridItem) {
          gridItem.classList.toggle("wishlist-popover-open", isOpen);
        }
      }
      displayWishlists() {
        if (!this.button) return;

        if (!this.wishlistPopover) {
          const returnUrl = encodeURIComponent(
            window.location.pathname +
              window.location.search +
              window.location.hash,
          );
          this.wishlistPopover = document.createElement("div");
          this.wishlistPopover.className = "add-to-wishlist-button__popover";
          this.wishlistPopover.innerHTML = `
            <p class="add-to-wishlist-button__popover-heading">Your Wishlists:</p>
           ${
              document.querySelector('input[data-user-logged-in="false"]')
                ? `<p class="add-to-wishlist-button__login-notice" role="status"><a href="${Shopify.routes.root}account/login?return_to=${returnUrl}">Login to save your wishlists</a></p>`
                : ""
            }
            <ul class="add-to-wishlist-button__popover-list custom-scrollbar custom-scrollbar-y"></ul>
            <button type="button" class="add-to-wishlist-button__new button button--tertiary">
              <span aria-hidden="true" class="add-to-wishlist-button__new-icon">+</span>
              <span>New Wishlist</span>
            </button>
            <div class="add-to-wishlist-button__create">
              <label class="visually-hidden" for="wishlist-name-${this.productId || "local"}">name</label>
              <input
                id="wishlist-name-${this.productId || "local"}"
                type="text"
                class="field__input add-to-wishlist-button__input"
                placeholder="Wishlist Name..."
              />
              <button type="button" class="button button--secondary add-to-wishlist-button__add">Add</button>
            </div>
          `;

          // When inside a product-slider, teleport the popover to document.body
          // so it escapes the slider's overflow-y:hidden and any transform
          // ancestors (e.g. the slideIn animation) that would trap fixed/absolute
          // positioning inside the clipped container.
          if (this.isInsideProductSlider()) {
            document.body.appendChild(this.wishlistPopover);
          } else {
            this.appendChild(this.wishlistPopover);
          }

          this.wishlistList = this.wishlistPopover.querySelector(
            ".add-to-wishlist-button__popover-list",
          );
          this.wishlistCreateContainer = this.wishlistPopover.querySelector(
            ".add-to-wishlist-button__create",
          );
          this.newWishlistButton = this.wishlistPopover.querySelector(
            ".add-to-wishlist-button__new",
          );
          this.addWishlistButton = this.wishlistPopover.querySelector(
            ".add-to-wishlist-button__add",
          );
          this.wishlistNameInput = this.wishlistPopover.querySelector(
            ".add-to-wishlist-button__input",
          );

          this.wishlistList.addEventListener("click", (event) => {
            const wishlistItem = event.target.closest(
              ".add-to-wishlist-button__popover-item",
            );
            if (!wishlistItem || !this.wishlistList.contains(wishlistItem))
              return;

            const wishlistName =
              wishlistItem.dataset.wishlistName ??
              wishlistItem.textContent.trim();
            const wishlistRowId = wishlistItem.dataset.wishlistRowId;
            if (!wishlistName) return;
            this.addToWishlist(wishlistName, wishlistRowId);
            this.closeWishlistPopover();
          });

          this.newWishlistButton.addEventListener("click", (event) => {
            event.preventDefault();
            this.wishlistCreateContainer.classList.toggle("is-visible");
            if (this.wishlistCreateContainer.classList.contains("is-visible")) {
              this.wishlistNameInput.focus();
            }
          });

          this.addWishlistButton.addEventListener("click", (event) => {
            event.preventDefault();
            const wishlistName = this.sanitizeWishlistName(
              this.wishlistNameInput.value,
            );
            this.addToWishlist(wishlistName, null);
            this.wishlistNameInput.value = "";
          });

          this.wishlistNameInput.addEventListener("input", () => {
            this.sanitizeWishlistNameInput();
          });

          this.wishlistNameInput.addEventListener("keydown", (event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            const wishlistName = this.sanitizeWishlistName(
              this.wishlistNameInput.value,
            );
            this.addToWishlist(wishlistName, null);
            this.wishlistNameInput.value = "";
          });
        }

        this.wishlistPopover.classList.toggle("is-visible");
        const isVisible = this.wishlistPopover.classList.contains("is-visible");
        this.togglePopoverStackingContext(isVisible);
        this.button.setAttribute("aria-expanded", isVisible ? "true" : "false");
        this.renderWishlistList();

        if (isVisible) {
          this.startCartMobilePopoverTracking();
        } else {
          this.stopCartMobilePopoverTracking();
        }

        if (!this.handleDocumentClickHandler) {
          this.handleDocumentClickHandler = (event) => {
            // When the popover is teleported to body (product-slider case),
            // it is no longer a descendant of `this`, so check it separately.
            if (this.contains(event.target)) return;
            if (this.wishlistPopover?.contains(event.target)) return;
            this.closeWishlistPopover();
          };
          document.addEventListener("click", this.handleDocumentClickHandler);
        }
      }

      loadLocalWishlists() {
        // get wishlists from localstorage
        let wishlists = localStorage.getItem("wishlists");
        if (wishlists) {
          try {
            const parsedWishlists = this.normalizeWishlists(
              JSON.parse(wishlists),
            );
            console.log("Local wishlists:", parsedWishlists);
            return parsedWishlists;
          } catch (error) {
            console.error("Failed to parse local wishlists", error);
          }
        }

        return [];
      }

      updateUiFromLocal() {
        if ((window.wishlists || []).length > 0) {
          const currentProductId = String(this.productId ?? "");
          this.productInWishlist = window.wishlists.some(
            (wishlist) =>
              Array.isArray(wishlist.items) &&
              wishlist.items.some((item) => {
                const itemProductId = String(
                  item?.product_id ?? item?.productId ?? "",
                );
                return itemProductId === currentProductId;
              }),
          );
          if (this.productInWishlist) {
            this.updateState("added");
            return true;
          }
        }
        return false;
      }

      async getWishlists() {
        this.setLoadingState();
        try {
          if (!this.customerId) {
            if (!Array.isArray(window.wishlists)) {
              window.wishlists = this.loadLocalWishlists();
            }
            this.updateUiFromLocal();
            return false;
          } else {
            try {
              await this.syncGuestWishlistsOnLogin();
            } catch (syncError) {
              console.error("Error syncing guest wishlist on login", syncError);
            }

            // Populate window.wishlists for the popover — fetched once per page load across all buttons
            if (!window.wishlistsForCustomerLoaded && !window.wishlistsLoadPromise) {
              window.wishlistsLoadPromise = this.fetchJson(
                "/endpoints/get_single_customer_wishlists.php",
                { shopifyCustomerId: this.customerId },
              ).then((data) => {
                window.wishlists = Array.isArray(data?.wishlists)
                  ? this.normalizeWishlists(data.wishlists)
                  : [];
                window.wishlistsForCustomerLoaded = true;
                window.wishlistsLoadPromise = null;
              }).catch(() => {
                if (!Array.isArray(window.wishlists)) window.wishlists = [];
                window.wishlistsForCustomerLoaded = true;
                window.wishlistsLoadPromise = null;
              });
            }

            const requestBody = {
              shopifyCustomerId: this.customerId,
              variantId: this.variantId,
            };

            const [itemResult] = await Promise.allSettled([
              this.fetchJson("/endpoints/get_single_item.php", requestBody),
              window.wishlistsLoadPromise || Promise.resolve(),
            ]);

            // handle item check in UI
            if (itemResult?.status === "fulfilled") {
              const itemData = itemResult.value;
              this.productInWishlist = Boolean(
                itemData?.in_wishlist === "true" ||
                itemData?.in_wishlist === true,
              );
            } else {
              console.error(
                "Error retrieving single wishlist item",
                itemResult?.reason,
              );
              this.productInWishlist = false;
            }

            this.updateState(this.productInWishlist ? "added" : "removed");
            return this.productInWishlist;
          }
        } catch (error) {
          console.error("Error getting wishlists", error);
          throw new Error("Something went wrong getting your wishlists");
        } finally {
          this.removeLoadingState();
        }
      }
      async addToWishlist(wishlistName, wishlistRowId) {
        wishlistName = this.sanitizeWishlistName(wishlistName);
        this.setLoadingState();
        try {
          if (!this.customerId) {
            let wishlistAdded = false;
            const wishlistIndex = (window.wishlists || []).findIndex(
              (wishlist) => wishlist.wishlist_name === wishlistName,
            );

            let wishlist;
            if (wishlistIndex === -1) {
              this.addWishlist(wishlistName);
              wishlist = window.wishlists.at(-1);
            } else {
              wishlist = window.wishlists[wishlistIndex];
            }
            if (wishlist) {
              const currentProductId = this.normalizeWishlistId(this.productId);
              const itemExists = wishlist.items.some(
                (item) =>
                  this.normalizeWishlistId(item?.product_id) ===
                  currentProductId,
              );

              if (!itemExists) {
                wishlist.items.push(this.buildWishlistItem());
              }

              this.saveWishlists();
              this.updateState("added");
              this.productInWishlist = true;
              this.updateWishlistViewer();
              this.updateHeaderButtonCount(true);
              wishlistAdded = true;
            }

            if (wishlistAdded) {
              await this.handleCartPageWishlistAdd();
            }

            return;
          }

          let wishlistAdded = false;
          try {
            const response = await fetch(
              "https://rarewaves-wishlist.etempashopifyapps-server-b.co.uk/endpoints/add_to_wishlist.php",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  shopifyCustomerId: this.customerId,
                  wishlistName: wishlistName,
                  wishlistRowId: wishlistRowId,
                  productId: this.dataset.productId,
                  variantId: this.dataset.variantId,
                  productHandle: this.dataset.productHandle,
                  onSale: this.onSale,
                }),
              },
            );
            if (!response.ok) {
              throw new Error(
                `Request failed (${response.status}) for add_to_wishlist.php`,
              );
            }

            const data = await response.json();
            if (data.failed) {
              throw new Error(data.error_message);
            }

            this.productInWishlist = true;
            wishlistAdded = true;
            this.closeWishlistPopover();
          } finally {
            this.updateState(this.productInWishlist ? "added" : "removed");
            this.updateHeaderButtonCount(true);
            this.updateWishlistViewer();
            if (wishlistAdded) {
              await this.handleCartPageWishlistAdd();
            }
          }
        } catch (error) {
          console.error("Error adding item to wishlist", error);
          throw new Error(
            "Something went wrong adding your product to the wishlist",
          );
        } finally {
          this.removeLoadingState();
        }
      }
      async removeFromWishlist() {
        this.setLoadingState();

        try {
          if (!this.customerId) {
            // remove from window.wishlists and the localstorage array
            const currentProductId = String(this.dataset.productId ?? "");
            window.wishlists = (window.wishlists || []).map((wishlist) => ({
              ...wishlist,
              items: Array.isArray(wishlist.items)
                ? wishlist.items.filter((item) => {
                    const itemProductId = String(
                      item?.product_id ?? item?.productId ?? "",
                    );
                    return itemProductId !== currentProductId;
                  })
                : [],
            }));
            this.saveWishlists();
            this.productInWishlist = false;
            this.updateState("removed");
            this.updateHeaderButtonCount(false);
            this.notifyWishlistViewer("item_removed");

            // update removedItems for next time sync
            const currentRemovedItems = localStorage.getItem(
              "wishlistsRemovedItems",
            );
            let currentRemovedItemsArray = [];

            // try to parse the currentRemovedItems or return an empty array
            try {
              const parsedRemovedItems = JSON.parse(
                currentRemovedItems ?? "[]",
              );
              currentRemovedItemsArray = Array.isArray(parsedRemovedItems)
                ? parsedRemovedItems
                : [];
            } catch (_error) {
              currentRemovedItemsArray = [];
            }

            localStorage.setItem(
              "wishlistsRemovedItems",
              JSON.stringify([...currentRemovedItemsArray, currentProductId]),
            );

            return;
          }

          try {
            const response = await fetch(
              "https://rarewaves-wishlist.etempashopifyapps-server-b.co.uk/endpoints/remove_from_wishlist.php",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  shopifyCustomerId: this.customerId,
                  variantId: this.dataset.variantId,
                }),
              },
            );
            if (!response.ok) {
              throw new Error(
                `Request failed (${response.status}) for remove_from_wishlist.php`,
              );
            }

            const data = await response.json();
            if (data.failed) {
              throw new Error(data.error_message);
            }

            this.productInWishlist = false;
          } finally {
            this.updateState(this.productInWishlist ? "added" : "removed");
            this.updateHeaderButtonCount(false);
            this.notifyWishlistViewer("item_removed");
          }
        } catch (error) {
          console.error("Error removing item to wishlist", error);
          throw new Error(
            "Something went wrong removing your product to the wishlist",
          );
        } finally {
          this.removeLoadingState();
        }
      }
      handleButtonClick(e) {
        e.preventDefault();
        if (this.button.classList.contains("loading")) return;
        if (this.productInWishlist) {
          this.removeFromWishlist();
          this.productInWishlist = false;
        } else {
          this.displayWishlists();
        }
      }
    },
  );
}
